import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { LayoutDashboard, Package, Receipt, Camera, ShoppingBag, Users, Truck, BarChart3, Settings, LogOut, LayoutTemplate, WifiOff, Banknote, RotateCcw, MessageCircle, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import ShopSwitcher from "@/components/ShopSwitcher";
import InstallPrompt from "@/components/InstallPrompt";

export default function Layout({ children }) {
  const { t, lang, setLang, user, logout } = useApp();
  const nav = useNavigate();
  const [online, setOnline] = React.useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [drawer, setDrawer] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => {
    const on = (e) => setBusy(!!e.detail);
    window.addEventListener("kirana:busy", on);
    return () => window.removeEventListener("kirana:busy", on);
  }, []);
  React.useEffect(() => {
    const onUp = () => setOnline(true), onDown = () => setOnline(false);
    window.addEventListener("online", onUp);
    window.addEventListener("offline", onDown);
    return () => { window.removeEventListener("online", onUp); window.removeEventListener("offline", onDown); };
  }, []);

  const navItems = [
    { to: "/", icon: LayoutDashboard, label: t("dashboard"), testid: "nav-dashboard" },
    { to: "/items", icon: Package, label: t("items"), testid: "nav-items" },
    { to: "/bills", icon: Receipt, label: t("bills"), testid: "nav-bills" },
    { to: "/photo-to-bill", icon: Camera, label: t("photo_bill"), testid: "nav-photo-bill" },
    { to: "/whatsapp-inbox", icon: MessageCircle, label: t("whatsapp_inbox"), testid: "nav-wa-inbox", ownerOnly: true },
    { to: "/returns", icon: RotateCcw, label: t("returns"), testid: "nav-returns" },
    { to: "/templates", icon: LayoutTemplate, label: t("templates"), testid: "nav-templates" },
    { to: "/purchases", icon: ShoppingBag, label: t("purchases"), testid: "nav-purchases" },
    { to: "/cash-drawer", icon: Banknote, label: t("cash_drawer"), testid: "nav-cash-drawer" },
    { to: "/customers", icon: Users, label: t("customers"), testid: "nav-customers" },
    { to: "/suppliers", icon: Truck, label: t("suppliers"), testid: "nav-suppliers" },
    { to: "/reports", icon: BarChart3, label: t("reports"), testid: "nav-reports" },
  ];

  const visibleNav = navItems.filter((n) => !n.ownerOnly || user?.role === "owner");
  const doLogout = () => { logout(); nav("/login"); };
  const closeDrawer = () => setDrawer(false);

  return (
    <div className="min-h-screen bg-[#FAF9F6] font-body">
      <header className="bg-white/90 backdrop-blur-md border-b border-stone-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <button onClick={() => setDrawer(true)} data-testid="hamburger-btn"
                    className="md:hidden h-10 w-10 rounded-xl grid place-items-center bg-stone-100 text-stone-700 shrink-0">
              <Menu className="w-5 h-5" />
            </button>
            <div className="w-10 h-10 rounded-xl bg-[#B45309] text-white grid place-items-center font-heading font-bold text-lg shrink-0" data-testid="app-logo">
              {t("logo_letter")}
            </div>
            <div className="hidden sm:block min-w-0">
              <div className="font-heading font-semibold text-lg text-stone-900 leading-none truncate" data-testid="app-name">{t("app_name")}</div>
              <div className="text-xs text-stone-500 mt-0.5 truncate">{t("tagline")}</div>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            {busy && (
              <div data-testid="sync-spinner" title="Syncing…" className="h-8 w-8 grid place-items-center">
                <div className="h-4 w-4 border-2 border-[#B45309] border-t-transparent rounded-full animate-spin" />
              </div>
            )}
            <ShopSwitcher />
            <div className="flex items-center gap-1 bg-stone-100 rounded-xl p-1" data-testid="lang-toggle">
              <button data-testid="lang-en" onClick={() => setLang("en")}
                      className={`px-2 sm:px-3 h-9 rounded-lg text-xs sm:text-sm font-medium ${lang === "en" ? "bg-white shadow text-stone-900" : "text-stone-600"}`}>EN</button>
              <button data-testid="lang-gu" onClick={() => setLang("gu")}
                      className={`px-2 sm:px-3 h-9 rounded-lg text-xs sm:text-sm font-medium font-gujarati ${lang === "gu" ? "bg-white shadow text-stone-900" : "text-stone-600"}`}>ગુ</button>
            </div>
            {user && (
              <div className="hidden lg:flex items-center gap-2 px-3 h-10 bg-stone-100 rounded-xl">
                <span className="text-xs text-stone-500 uppercase tracking-widest">{user.role}</span>
                <span className="text-sm font-medium text-stone-800">{user.name}</span>
              </div>
            )}
            <Button variant="ghost" size="icon" onClick={doLogout} data-testid="logout-btn" className="h-10 w-10 sm:h-11 sm:w-11">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto flex">
        <aside className="hidden md:block w-56 shrink-0 py-6 pl-4">
          <nav className="space-y-1 sticky top-24">
            {visibleNav.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={n.testid}
                className={({ isActive }) => `flex items-center gap-3 px-3 h-12 rounded-xl text-sm font-medium ${isActive ? "bg-[#FFF7ED] text-[#B45309]" : "text-stone-700 hover:bg-stone-100"}`}>
                <n.icon className="w-5 h-5" /> {n.label}
              </NavLink>
            ))}
            <NavLink to="/settings" data-testid="nav-settings" className={({ isActive }) => `flex items-center gap-3 px-3 h-12 rounded-xl text-sm font-medium mt-4 ${isActive ? "bg-[#FFF7ED] text-[#B45309]" : "text-stone-700 hover:bg-stone-100"}`}>
              <Settings className="w-5 h-5" /> {t("settings")}
            </NavLink>
          </nav>
        </aside>

        <main className="flex-1 min-w-0 p-4 md:p-6 pb-10">
          {!online && (
            <div className="mb-4 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm flex items-center gap-2" data-testid="offline-banner">
              <WifiOff className="w-4 h-4" /> {t("offline_now")}
            </div>
          )}
          {children}
        </main>
      </div>

      {/* Mobile drawer */}
      {drawer && (
        <div className="md:hidden fixed inset-0 z-50" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={closeDrawer} />
          <div className="absolute left-0 top-0 bottom-0 w-72 max-w-[85%] bg-white shadow-xl flex flex-col animate-in slide-in-from-left duration-200">
            <div className="h-16 border-b border-stone-200 flex items-center justify-between px-4">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-lg bg-[#B45309] text-white grid place-items-center font-heading font-bold">{t("logo_letter")}</div>
                <div className="font-heading font-semibold">{t("app_name")}</div>
              </div>
              <button onClick={closeDrawer} data-testid="drawer-close" className="h-10 w-10 rounded-lg grid place-items-center bg-stone-100 text-stone-700"><X className="w-5 h-5" /></button>
            </div>
            {user && (
              <div className="px-4 py-3 bg-stone-50 border-b border-stone-200">
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#B45309]">{user.role}</div>
                <div className="font-medium text-stone-900 truncate">{user.name}</div>
              </div>
            )}
            <nav className="flex-1 overflow-y-auto p-3 space-y-1">
              {visibleNav.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`${n.testid}-drawer`} onClick={closeDrawer}
                  className={({ isActive }) => `flex items-center gap-3 px-3 h-12 rounded-xl text-sm font-medium ${isActive ? "bg-[#FFF7ED] text-[#B45309]" : "text-stone-700 hover:bg-stone-100"}`}>
                  <n.icon className="w-5 h-5" /> {n.label}
                </NavLink>
              ))}
              <NavLink to="/settings" data-testid="nav-settings-drawer" onClick={closeDrawer}
                className={({ isActive }) => `flex items-center gap-3 px-3 h-12 rounded-xl text-sm font-medium mt-3 border-t border-stone-100 pt-4 ${isActive ? "bg-[#FFF7ED] text-[#B45309]" : "text-stone-700 hover:bg-stone-100"}`}>
                <Settings className="w-5 h-5" /> {t("settings")}
              </NavLink>
            </nav>
            <div className="p-3 border-t border-stone-200">
              <button onClick={() => { closeDrawer(); doLogout(); }} data-testid="drawer-logout"
                      className="w-full h-11 rounded-xl bg-red-50 hover:bg-red-100 text-red-700 font-semibold flex items-center justify-center gap-2 text-sm">
                <LogOut className="w-4 h-4" /> {t("logout")}
              </button>
            </div>
          </div>
        </div>
      )}
      <InstallPrompt />
    </div>
  );
}
